/**
 * Job location for matching - the form-independent half.
 *
 * Everything here is plain data and pure functions so any form (today's
 * HireNowModal, tomorrow's post-a-role wizard) can hold a `JobLocationValue`
 * in its own state, render <JobLocationFields/> for it, and call
 * `toJobLocationPayload` on submit. The vocabulary (modes, scopes, continents,
 * regions) mirrors the API's Services/LocationMatch.js and Services/Regions.js;
 * the API validates again, so drift shows up as a 422 rather than bad data.
 */

export type WorkMode = "onsite" | "hybrid" | "remote" | "field" | "rotational";
export type RemoteScope = "country" | "continent" | "region" | "global";

/** A city-level place, as matching compares it. */
export type JobCity = {
  city: string;
  state: string;
  country: string;
  country_code: string;
};

export type JobCountry = { country: string; country_code: string };

export type JobLocationValue = {
  workMode: WorkMode | null;
  /** Only meaningful when workMode is "remote". */
  remoteScope: RemoteScope | null;
  /** Onsite-like modes. */
  city: JobCity | null;
  /** remoteScope "country". */
  country: JobCountry | null;
  /** remoteScope "continent". */
  continent: string | null;
  /** remoteScope "region". */
  region: string | null;
};

export const WORK_MODES: { id: WorkMode; label: string; description: string }[] = [
  { id: "onsite", label: "Onsite", description: "Works from your premises every day" },
  { id: "hybrid", label: "Hybrid", description: "Some days onsite, some days remote" },
  { id: "remote", label: "Remote", description: "Works from anywhere in scope" },
  { id: "field", label: "Field / Mobile", description: "Moves between sites, outlets or customers" },
  { id: "rotational", label: "Rotational", description: "On/off rotation, e.g. 14/14 at a site" },
];

/** Modes that put the talent at the job's address at least some of the time. */
export const ONSITE_MODES: WorkMode[] = ["onsite", "hybrid", "field", "rotational"];

export const REMOTE_SCOPES: { id: RemoteScope; label: string; prompt: string }[] = [
  { id: "country", label: "Country", prompt: "Which country?" },
  { id: "continent", label: "Continent", prompt: "Which continent?" },
  { id: "region", label: "Region", prompt: "Which region?" },
  { id: "global", label: "Globe", prompt: "Anywhere in the world" },
];

export const CONTINENTS = [
  "Africa", "Asia", "Europe", "North America", "South America", "Oceania", "Antarctica",
];

export const REGIONS = [
  "West Africa", "East Africa", "Southern Africa", "North Africa", "Central Africa",
  "Middle East", "Western Europe", "Eastern Europe", "North America",
  "Latin America & Caribbean", "South Asia", "Southeast Asia", "East Asia", "Oceania",
];

export const emptyJobLocation = (): JobLocationValue => ({
  workMode: null,
  remoteScope: null,
  city: null,
  country: null,
  continent: null,
  region: null,
});

export const isOnsiteMode = (mode: WorkMode | null) => !!mode && ONSITE_MODES.includes(mode);

/** The first thing wrong with the value, or null when it can be submitted. */
export const validateJobLocation = (v: JobLocationValue): string | null => {
  if (!v.workMode) return "Choose how the talent will work";
  if (isOnsiteMode(v.workMode)) {
    if (!v.city) return "Pick the city the talent will work from";
    return null;
  }
  // remote
  if (!v.remoteScope) return "Choose where remote talents can be matched from";
  if (v.remoteScope === "country" && !v.country) return "Pick a country";
  if (v.remoteScope === "continent" && !v.continent) return "Pick a continent";
  if (v.remoteScope === "region" && !v.region) return "Pick a region";
  return null;
};

/** The camelCase fields hireContact and jobs/:id/edit accept. Unused fields are sent blank so an edit clears them. */
export const toJobLocationPayload = (v: JobLocationValue) => {
  const onsite = isOnsiteMode(v.workMode);
  const remote = v.workMode === "remote";
  const scope = remote ? v.remoteScope : null;
  return {
    workMode: v.workMode ?? "",
    remoteScope: scope ?? "",
    jobCity: onsite ? v.city?.city ?? "" : "",
    jobState: onsite ? v.city?.state ?? "" : "",
    jobCountry: onsite ? v.city?.country ?? "" : scope === "country" ? v.country?.country ?? "" : "",
    jobCountryCode: onsite ? v.city?.country_code ?? "" : scope === "country" ? v.country?.country_code ?? "" : "",
    jobContinent: scope === "continent" ? v.continent ?? "" : "",
    jobRegion: scope === "region" ? v.region ?? "" : "",
  };
};

/** Rebuild the value from a job row (snake_case columns) for editing. */
export const fromJobRow = (row: any): JobLocationValue => {
  const v = emptyJobLocation();
  if (!row) return v;
  v.workMode = (row.work_mode as WorkMode) ?? null;
  v.remoteScope = (row.remote_scope as RemoteScope) ?? null;
  if (isOnsiteMode(v.workMode) && row.job_city) {
    v.city = {
      city: row.job_city,
      state: row.job_state ?? "",
      country: row.job_country ?? "",
      country_code: row.job_country_code ?? "",
    };
  }
  if (v.remoteScope === "country" && row.job_country_code) {
    v.country = { country: row.job_country ?? row.job_country_code, country_code: row.job_country_code };
  }
  v.continent = row.job_continent ?? null;
  v.region = row.job_region ?? null;
  return v;
};

export const cityLabel = (c: JobCity | null | undefined) =>
  c ? [c.city, c.state, c.country].filter(Boolean).join(", ") : "";

/** "Onsite · Port Harcourt, Rivers, Nigeria" / "Remote · West Africa" / "Remote · Worldwide". Empty for legacy jobs. */
export const describeJobLocation = (source: JobLocationValue | any): string => {
  const v: JobLocationValue = source && "workMode" in source ? source : fromJobRow(source);
  if (!v.workMode) return "";
  const mode = WORK_MODES.find((m) => m.id === v.workMode)?.label ?? v.workMode;
  if (isOnsiteMode(v.workMode)) return [mode, cityLabel(v.city)].filter(Boolean).join(" · ");
  const where =
    v.remoteScope === "country" ? v.country?.country
    : v.remoteScope === "continent" ? v.continent
    : v.remoteScope === "region" ? v.region
    : v.remoteScope === "global" ? "Worldwide"
    : "";
  return [mode, where].filter(Boolean).join(" · ");
};
